import { Link } from "react-router-dom";
import {
  Globe, Search, Calendar, Shield, Smartphone, BarChart2,
  Zap, Code2, Palette, Headphones, ArrowRight, Check
} from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { SEOHead } from "@/components/SEOHead";

const SOLUTIONS = [
  {
    icon: <Globe size={28} />,
    title: "Professionelle Website",
    tagline: "Ihr digitales Zuhause",
    desc: "Wir erstellen Ihre Website von Grund auf — mit modernem Design, klarer Struktur und überzeugenden Texten. Kein Template-Baukastsein, sondern eine Website die zu Ihrem Unternehmen passt.",
    details: [
      "Individuelle Gestaltung je nach Branche und Stil",
      "Bis zu unbegrenzte Unterseiten (je nach Paket)",
      "Professionelle Texte die Kunden überzeugen",
      "Schnelle Ladezeiten und technische Optimierung",
    ],
    color: "from-blue-600/20 to-cyan-600/10",
    accent: "text-blue-400",
  },
  {
    icon: <Search size={28} />,
    title: "SEO & Sichtbarkeit",
    tagline: "Gefunden werden auf Google",
    desc: "Eine Website die niemand findet bringt nichts. Wir sorgen dafür, dass Ihr Unternehmen bei lokalen Suchanfragen ganz oben erscheint — dauerhaft und nachhaltig.",
    details: [
      "On-Page SEO von Anfang an richtig aufgesetzt",
      "Lokale Suchmaschinenoptimierung für Ihre Region",
      "Google My Business Optimierung und Einrichtung",
      "Monatliche SEO-Reports (Business & Premium)",
    ],
    color: "from-green-600/20 to-emerald-600/10",
    accent: "text-green-400",
  },
  {
    icon: <Calendar size={28} />,
    title: "Online-Buchungssystem",
    tagline: "Termine rund um die Uhr",
    desc: "Kunden buchen direkt auf Ihrer Website — zu jeder Tages- und Nachtzeit. Kein Telefontagebuch mehr. Kein verpasster Termin mehr. Nur zufriedene Kunden.",
    details: [
      "Individuell konfigurierbare Services und Zeiten",
      "Automatische Bestätigungs-E-Mails an Kunden",
      "Kalender-Integration (.ics Export)",
      "Echtzeit-Buchungsübersicht im Admin-Bereich",
    ],
    color: "from-purple-600/20 to-violet-600/10",
    accent: "text-purple-400",
  },
  {
    icon: <Smartphone size={28} />,
    title: "Mobile-First Design",
    tagline: "Perfekt auf jedem Gerät",
    desc: "Über 60% Ihrer potenziellen Kunden besuchen Websites über das Smartphone. Wir stellen sicher, dass Ihre Website auf allen Geräten perfekt aussieht und funktioniert.",
    details: [
      "Responsive Design für alle Bildschirmgrößen",
      "Touch-optimierte Benutzeroberfläche",
      "Schnelle Ladezeiten auf mobilem Internet",
      "Google Mobile-Freundlichkeits-Test bestanden",
    ],
    color: "from-cyan-600/20 to-sky-600/10",
    accent: "text-cyan-400",
  },
  {
    icon: <Shield size={28} />,
    title: "DSGVO & Sicherheit",
    tagline: "Rechtlich auf der sicheren Seite",
    desc: "Datenschutz ist kein Nice-to-have — er ist Pflicht. Wir richten Ihre Website so ein, dass sie alle deutschen und europäischen Datenschutzanforderungen erfüllt.",
    details: [
      "DSGVO-konformes Cookie-Banner",
      "Datenschutzerklärung und Impressum inklusive",
      "SSL-Zertifikat (HTTPS) inklusive",
      "DSGVO-konforme Formular-Einbindung",
    ],
    color: "from-red-600/20 to-rose-600/10",
    accent: "text-red-400",
  },
  {
    icon: <BarChart2 size={28} />,
    title: "Analytics & Reporting",
    tagline: "Zahlen die zählen",
    desc: "Was bringt Ihre Website wirklich? Mit unserem Reporting wissen Sie genau, wie viele Besucher kommen, woher sie stammen und was sie machen.",
    details: [
      "Besucher-Statistiken in Echtzeit",
      "Herkunftsanalyse (Google, Direkt, Social)",
      "Monatliche Performance-Berichte per E-Mail",
      "Conversion-Tracking für Kontaktanfragen",
    ],
    color: "from-amber-600/20 to-yellow-600/10",
    accent: "text-amber-400",
  },
];

export default function LoesungenPage() {
  return (
    <PublicLayout>
      <SEOHead
        title="Unsere Lösungen"
        description="Website-Erstellung, SEO, Online-Buchungssystem, Mobile-First Design, DSGVO-Konformität und Analytics — alles aus einer Hand für lokale Unternehmen."
        canonical="/loesungen"
      />
      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-4">Unsere Lösungen</p>
            <h1
              className="font-black text-5xl sm:text-6xl text-white mb-6 leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Alles aus einer Hand.
            </h1>
            <p className="text-xl text-white/50 leading-relaxed">
              Von der Website-Erstellung über SEO bis zum Online-Buchungssystem — signalSolution liefert alles was Ihr Unternehmen für eine erfolgreiche Online-Präsenz braucht.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto space-y-8">
          {SOLUTIONS.map((sol, i) => (
            <div
              key={sol.title}
              className={`bg-[#0A1220] border border-white/5 rounded-2xl overflow-hidden hover:border-white/10 transition-all`}
            >
              <div className={`grid md:grid-cols-2 ${i % 2 === 1 ? "md:[&>*:first-child]:order-last" : ""}`}>
                {/* Content */}
                <div className="p-8 lg:p-10">
                  <div className={`w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center ${sol.accent} mb-5`}>
                    {sol.icon}
                  </div>
                  <p className={`text-sm font-semibold uppercase tracking-widest ${sol.accent} mb-2`}>{sol.tagline}</p>
                  <h2
                    className="font-black text-3xl text-white mb-4"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {sol.title}
                  </h2>
                  <p className="text-white/55 leading-relaxed mb-7 text-sm sm:text-base">{sol.desc}</p>
                  <ul className="space-y-2.5">
                    {sol.details.map((d) => (
                      <li key={d} className="flex items-start gap-2.5 text-sm text-white/65">
                        <Check size={15} className={`flex-shrink-0 mt-0.5 ${sol.accent}`} />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Visual */}
                <div className={`bg-gradient-to-br ${sol.color} min-h-[200px] md:min-h-0 relative flex items-center justify-center p-8`}>
                  <div className="text-center">
                    <div className={`w-20 h-20 rounded-3xl bg-black/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 ${sol.accent}`}>
                      <span className="scale-[1.6]">{sol.icon}</span>
                    </div>
                    <p className="font-bold text-white/70 text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {sol.title}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-6 bg-[#070d1a]">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-4">Unser Prozess</p>
          <h2
            className="font-black text-4xl text-white mb-16"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Von der Anfrage zur Live-Website
          </h2>

          <div className="relative">
            <div className="hidden md:block absolute top-6 left-12 right-12 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: "1", title: "Gespräch", desc: "Kostenloses Erstgespräch — wir lernen Ihr Unternehmen kennen" },
                { step: "2", title: "Demo in 24h", desc: "Individuelle Demo-Website basierend auf Ihrer Branche" },
                { step: "3", title: "Freigabe", desc: "Sie prüfen und geben Feedback — wir setzen alles um" },
                { step: "4", title: "Live in 48h", desc: "Ihre Website geht online — Sie sind startklar" },
              ].map((s) => (
                <div key={s.step} className="relative">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mx-auto mb-4">
                    <span className="font-black text-cyan-400">{s.step}</span>
                  </div>
                  <h3 className="font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-white/40">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2
            className="font-black text-4xl text-white mb-5"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Bereit loszulegen?
          </h2>
          <p className="text-white/50 mb-8 text-lg">
            Holen Sie sich jetzt Ihre kostenlose Demo-Website.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt#demo-form"
              className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-xl shadow-cyan-400/20"
            >
              Demo anfragen <ArrowRight size={18} />
            </Link>
            <Link
              to="/leistungen"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 text-white font-medium rounded-xl hover:bg-white/10 transition-all border border-white/10"
            >
              Preise ansehen
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
